/**
 * Count the Outs quiz. Procedural board + hand generator across 6
 * scenario types (flush draw, OESD, gutshot, set, two overcards, combo).
 */

import { PlayingCard } from "@/features/chat/components/PlayingCard";
import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import {
  type CountOutsQuestion,
  generateQuestion,
} from "@/features/quiz/lib/countOutsQuiz";

export function QuizCountOuts() {
  const { question, revealedAnswer, isCorrect, correctAnswer, stats, submit, next } =
    useQuizSession<CountOutsQuestion, number>({
      quizType: "count_outs",
      generate: generateQuestion,
      getCorrectAnswer: (q) => q.options[q.correctIndex]!,
      getQuestionPayload: (q) => ({
        hand: q.hand,
        board: q.board,
        target: q.target,
      }),
    });

  return (
    <QuizLayout
      Icon={Brain}
      title="Count the Outs"
      subtitle="Flop draws: flush, straight, set"
      description="Tests recognition of common flop draws and the number of outs to improve. Every table-time equity calculation starts with an out count; misreading the draw poisons every downstream decision (pot odds, raise/fold, bet sizing)."
      stats={stats}
    >
      <div className="flex flex-col items-center gap-2">
        <div className="text-[10px] uppercase tracking-wider text-text-muted">Board</div>
        <div className="flex items-center gap-2">
          {question.board.map((token, i) => (
            <PlayingCard key={`b-${i}-${token}`} token={token} size="md" />
          ))}
        </div>
      </div>

      <div className="flex flex-col items-center gap-2">
        <div className="text-[10px] uppercase tracking-wider text-text-muted">Your hand</div>
        <div className="flex items-center gap-2">
          <PlayingCard token={question.hand[0]} size="lg" />
          <PlayingCard token={question.hand[1]} size="lg" />
        </div>
      </div>

      <p className="text-center text-text text-base">
        How many outs do you have to <strong>{question.targetLabel}</strong>?
      </p>

      <div className="grid grid-cols-4 gap-3">
        {question.options.map((n) => (
          <QuizOption
            key={n}
            value={n}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
          >
            <span className="text-lg">{n}</span>
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>{correctAnswer} outs</strong>
            </>
          }
          rationale={question.rationale}
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}
